import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Common } from "@src/styles/Common";

import MgInput from "@src/components/input/MgInput";
import ExpertSelectInfo from "@src/components/SignupExpert/ExpertSelectInfo";
import UserInfoProfileUploadContainer from "@src/components/SignupExpert/Containers/UserInfoProfileUploader";
import MgDialog from "@src/components/Dialog";
import Header from "@src/components/Header";
import UserOfficeInfoBox from "@src/components/SignupExpert/UserOfficeInfoCard";
import ImageModal from "./ui/ImageModal";

import { transferToNative, WebAppBridge, getDeviceType } from "@src/lib/WebAppBridge";
import { usePutExpertInfo, useGetExpertApprovalStatus, useGetExpertInfo } from "./service/expert.api";
import type { ExpertInfoItem as ApiExpertInfoItem, CounselingCategory } from "./service/expert.type";
import { validateExpertForm } from "./validation";
import { COUNCELING_CATEGORIES_ORDER } from "@src/pages/expert/ExpertApplication";

import ApproveWait from "@src/assets/ApproveWait.png";

const DISPLAY_TO_SERVER: Record<string, string> = {
    매수신청대리인: "경매대행",
};

const buildInitialFormInfo = (): ApiExpertInfoItem => ({
    phone: "",
    counselingCategories: [],
    updatedAt: new Date().toISOString(),
    type: "",
    career: {},
    qualification: "",
    introduction: "",
    auctionAgentCertFileUrl: "",
    bankName: "",
    bankAccount: "",
});

const toNumberCareer = (career: ApiExpertInfoItem["career"]): { [key: string]: number } => {
    const parsed = typeof career === "string" ? JSON.parse(career) : career ?? {};
    return Object.fromEntries(
        Object.entries(parsed)
            .filter(([k]) => isNaN(Number(k)))
            .map(([k, v]) => [k, Number(v) || 0]),
    );
};
const toServerCareer = (career: ApiExpertInfoItem["career"]): { [key: string]: number } => {
    const parsed = typeof career === "string" ? JSON.parse(career) : career ?? {};
    return Object.fromEntries(
        Object.entries(parsed)
            .filter(([k]) => typeof k === "string" && isNaN(Number(k))) // 숫자 key 제거
            .map(([k, v]) => [DISPLAY_TO_SERVER[k] ?? k, Number(v) || 0]),
    );
};

const parseCounselingCategories = (raw: unknown): CounselingCategory[] => {
    if (Array.isArray(raw)) return raw as CounselingCategory[];
    if (typeof raw === "string") {
        try {
            const p = JSON.parse(raw);
            return Array.isArray(p) ? (p as CounselingCategory[]) : [];
        } catch {
            return [];
        }
    }
    return [];
};

const ExpertInfos = () => {
    const [expertNo, setExpertNo] = useState(0);
    const [profile, setProfile] = useState("");
    const [formInfo, setFormInfo] = useState<ApiExpertInfoItem>(buildInitialFormInfo);
    const [isEditing, setIsEditing] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [validationError, setValidationError] = useState("");

    const { data, isLoading, error, refetch } = useGetExpertInfo();
    const { data: approvalStatus } = useGetExpertApprovalStatus();
    const { mutate: updateExpertInfo, error: submitError, reset: resetSubmitError } = usePutExpertInfo();

    useEffect(() => {
        if (isLoading || error || !data || isEditing) return;

        const info = data.info as ApiExpertInfoItem & {
            auctionAgentCareer?: number | null;
            consultingCareer?: number | null;
            companionCareer?: number | null;
            agentCareer?: number | null;
        };

        setExpertNo(data.expertNo);
        setProfile(data.profile ?? "");
        setFormInfo({
            ...info,
            counselingCategories: parseCounselingCategories(info.counselingCategories).filter((c) =>
                COUNCELING_CATEGORIES_ORDER.includes(c),
            ),
            career: {
                ...(info.auctionAgentCareer ? { 매수신청대리인: info.auctionAgentCareer } : {}),
                ...(info.consultingCareer ? { 매물컨설팅: info.consultingCareer } : {}),
                ...(info.companionCareer ? { 임장동행: info.companionCareer } : {}),
                ...(info.agentCareer ? { 중개: info.agentCareer } : {}),
            },
            qualification: info.qualification ?? "",
            introduction: info.introduction ?? "",
            auctionAgentCertFileUrl: info.auctionAgentCertFileUrl ?? "",
            bankName: info.bankName ?? "",
            bankAccount: info.bankAccount ?? "",
        });
    }, [data, isLoading, error, isEditing]);

    useEffect(() => {
        if (getDeviceType() !== "android") return;

        WebAppBridge.onGetCertificateImage = (imageUrl: string) => {
            setFormInfo((prev) => ({ ...prev, auctionAgentCertFileUrl: imageUrl }));
        };

        return () => {
            WebAppBridge.onGetCertificateImage = undefined as any;
        };
    }, []);

    const updateFormInfo = (partial: Partial<ApiExpertInfoItem>) => {
        setFormInfo((prev) => ({ ...prev, ...partial }));
    };

    const handleSubmit = () => {
        const validation = validateExpertForm({
            counselingCategories: formInfo.counselingCategories,
            careerObj: toNumberCareer(formInfo.career),
            bankName: formInfo.bankName,
            bankAccount: formInfo.bankAccount,
            officeInfo: formInfo.officeInfo,
            auctionAgentCertFileUrl: formInfo.auctionAgentCertFileUrl,
        });

        if (!validation.valid) {
            setValidationError(validation.error ?? "입력값을 확인해주세요.");
            return;
        }

        const isAuctionAgent = formInfo.counselingCategories.includes("매수신청대리인");
        const needsOffice = formInfo.counselingCategories.includes("중개") || isAuctionAgent;

        updateExpertInfo(
            {
                expertNo,
                phone: formInfo.phone,
                profile,
                officeInfo: needsOffice ? formInfo.officeInfo : undefined,
                counselingCategories: formInfo.counselingCategories,
                career: toServerCareer(formInfo.career),
                qualification: formInfo.qualification ?? "",
                introduction: formInfo.introduction ?? "",
                isAuctionAgent,
                auctionAgentCertFileUrl: formInfo.auctionAgentCertFileUrl ?? "",
                bankName: formInfo.bankName ?? "",
                bankAccount: formInfo.bankAccount ?? "",
            },
            {
                onSuccess: () => {
                    setIsEditing(false);
                    refetch();
                },
            },
        );
    };

    const handleEditClick = () => {
        document.querySelector(".expert-container")?.scrollTo({ top: 0, behavior: "smooth" });
        setIsEditing(true);
    };

    if (isLoading || !data) return <div />;

    const isAuctionAgent = formInfo.counselingCategories.includes("매수신청대리인");
    const includesOfficeCategory = formInfo.counselingCategories.includes("중개") || isAuctionAgent;
    const isApproved = approvalStatus?.isApproved ?? false;
    const isRejected = approvalStatus?.isRejected ?? false;
    const errorMessage = validationError || (submitError instanceof Error ? submitError.message : String(submitError ?? ""));

    return (
        <Background>
            <Header
                title={isEditing ? "전문가 회원 정보 수정" : "전문가 회원 정보"}
                hasCloseBtn={false}
                onClose={() => transferToNative("done", "closeWebView")}
            />

            <ExpertContainer className="expert-container" as="form" onSubmit={(e) => e.preventDefault()}>
                {/* 프로필 */}
                <InputBox className="profile-box">
                    <UserInfoProfileUploadContainer
                        profile={profile}
                        isEditing={isEditing}
                        onProfileChange={(url) => setProfile(url)}
                    />
                </InputBox>

                {/* 전문가 유형 */}
                <InputBox>
                    <SectionHeaderWrap>
                        {isEditing && <RequiredMark>*</RequiredMark>}
                        <SectionTitle>{isEditing ? "원하는 전문가 (복수선택 가능)" : "전문가"}</SectionTitle>
                    </SectionHeaderWrap>

                    {!isEditing && isAuctionAgent && !isApproved && (
                        <ApproveCheckContainer>
                            <ApproveCheckContentWrapper>
                                <ApproveCheckIconImage src={ApproveWait} alt="승인 상태" />
                                <ApproveCheckStatusText>
                                    {isRejected
                                        ? `매수 신청 대리인 등록 승인이 거절되었습니다.\n매수신청대리인 등록증을 새롭게 추가해주세요`
                                        : "매수 신청 대리인 등록 승인이 현재 대기 상태입니다."}
                                </ApproveCheckStatusText>
                            </ApproveCheckContentWrapper>
                        </ApproveCheckContainer>
                    )}

                    <ExpertSelectInfo
                        isApproved={isApproved}
                        availableCategories={COUNCELING_CATEGORIES_ORDER}
                        isEditing={isEditing}
                        counselingCategories={formInfo.counselingCategories}
                        career={toNumberCareer(formInfo.career)}
                        officeInfo={formInfo.officeInfo}
                        auctionAgentCertFileUrl={formInfo.auctionAgentCertFileUrl}
                        onCategoriesChange={(categories, career) => updateFormInfo({ counselingCategories: categories, career })}
                        onCertFileChange={(url) => updateFormInfo({ auctionAgentCertFileUrl: url })}
                    />
                </InputBox>

                {/* 중개사무소 (조회 모드만) */}
                {includesOfficeCategory && !isEditing && formInfo.officeInfo && (
                    <InputBox>
                        <SectionHeaderWrap>
                            <SectionTitle>중개사무소 정보</SectionTitle>
                        </SectionHeaderWrap>
                        <UserOfficeInfoBox officeInfo={formInfo.officeInfo} />
                    </InputBox>
                )}

                {/* 매수신청대리인 등록증 (조회 모드만) */}
                {isAuctionAgent && formInfo.auctionAgentCertFileUrl && !isEditing && (
                    <InputBox>
                        <SectionHeaderWrap>
                            <SectionTitle>매수신청대리인 등록증 정보</SectionTitle>
                        </SectionHeaderWrap>
                        <CertPreviewWrap>
                            <CertPreviewImg
                                src={formInfo.auctionAgentCertFileUrl}
                                alt="매수신청대리인 등록증 미리보기"
                                onClick={() => setShowImageModal(true)}
                            />
                        </CertPreviewWrap>
                    </InputBox>
                )}

                {showImageModal && (
                    <ImageModal imageUrl={formInfo.auctionAgentCertFileUrl ?? ""} onClose={() => setShowImageModal(false)} />
                )}

                {/* 경력 */}
                {formInfo.counselingCategories.length > 0 && (
                    <InputBox>
                        <SectionHeaderWrap>
                            {isEditing && <RequiredMark>*</RequiredMark>}
                            <SectionTitle>{isEditing ? "경력 (시작 연도를 입력해주세요)" : "경력(시작 연도)"}</SectionTitle>
                        </SectionHeaderWrap>

                        {COUNCELING_CATEGORIES_ORDER.filter((cat) => formInfo.counselingCategories.includes(cat)).map((cat) => (
                            <YearRow key={cat}>
                                <YearLabel>{cat}</YearLabel>
                                <YearInputWrap>
                                    <MgInput
                                        width="100%"
                                        value={formInfo.career?.[cat] || ""}
                                        onChange={(e: any) =>
                                            updateFormInfo({
                                                career: {
                                                    ...formInfo.career,
                                                    [cat]: Number(e.target.value) || 0,
                                                },
                                            })
                                        }
                                        maxLength={4}
                                        placeholder="YYYY"
                                        noBorder
                                        bgColor="#FBF8F8"
                                        inputMode="numeric"
                                        readOnly={!isEditing}
                                    />
                                    <YearSuffix>년</YearSuffix>
                                </YearInputWrap>
                            </YearRow>
                        ))}
                    </InputBox>
                )}

                {/* 계좌번호 */}
                <InputBox>
                    <SectionHeaderWrap>
                        {isEditing && <RequiredMark>*</RequiredMark>}
                        <SectionTitle>{isEditing ? "계좌번호 정보" : "계좌번호"}</SectionTitle>
                    </SectionHeaderWrap>
                    <MgInput
                        value={formInfo.bankName ?? ""}
                        onChange={(e: any) => updateFormInfo({ bankName: e.target.value })}
                        maxLength={20}
                        placeholder="은행명을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    <MgInput
                        value={formInfo.bankAccount ?? ""}
                        onChange={(e: any) => updateFormInfo({ bankAccount: e.target.value })}
                        maxLength={20}
                        placeholder="계좌번호를 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                    />
                </InputBox>

                {/* 자격 */}
                <InputBox $relative>
                    <SectionHeaderWrap>
                        <SectionTitle>{isEditing ? "자격 (선택)" : "자격"}</SectionTitle>
                    </SectionHeaderWrap>
                    <MgInput
                        as="textarea"
                        value={formInfo.qualification ?? ""}
                        onChange={(e: any) => updateFormInfo({ qualification: e.target.value })}
                        maxLength={200}
                        placeholder="자격 사항을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                        height="200px"
                    />
                    <LimitText $visible={isEditing}>{(formInfo.qualification ?? "").length} / 200</LimitText>
                </InputBox>

                {/* 소개 */}
                <InputBox $relative>
                    <SectionHeaderWrap>
                        <SectionTitle>{isEditing ? "소개 (선택)" : "소개"}</SectionTitle>
                    </SectionHeaderWrap>
                    <MgInput
                        as="textarea"
                        value={formInfo.introduction ?? ""}
                        onChange={(e: any) => updateFormInfo({ introduction: e.target.value })}
                        maxLength={500}
                        placeholder="소개 글을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                        height="200px"
                    />
                    <LimitText $visible={isEditing}>{(formInfo.introduction ?? "").length} / 500</LimitText>
                </InputBox>

                <BottomSpace />

                <SubmitButton type="button" onClick={isEditing ? handleSubmit : handleEditClick}>
                    {isEditing ? "수정 완료" : "수정하기"}
                </SubmitButton>

                {errorMessage && (
                    <MgDialog
                        open
                        onCloseView={() => {
                            setValidationError("");
                            resetSubmitError();
                        }}
                        contents={<p>{errorMessage}</p>}
                        confirmText="확인"
                    />
                )}
            </ExpertContainer>
        </Background>
    );
};

export default ExpertInfos;

// ─── Styled Components ────────────────────────────────────────────────────────

const Background = styled.div`
    position: relative;
    width: 100%;
    min-height: 100vh;
    overflow: hidden;
`;

const ExpertContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    padding: 24px;
    overflow-y: scroll;
    background: #ffffff;
    min-height: calc(100vh - 48px);

    @media screen and (min-width: 768px) {
        position: absolute;
        width: 336px;
        min-height: 700px;
        top: 60px;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 12px;
        overflow-y: auto;
    }
`;

const InputBox = styled.div<{ $relative?: boolean }>`
    position: ${({ $relative }) => ($relative ? "relative" : "static")};
    margin-top: 16px;
    margin-bottom: 24px;

    &.profile-box {
        display: flex;
        justify-content: center;
    }

    & > input + input {
        margin-top: 8px;
    }
`;

const SectionHeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
`;

const RequiredMark = styled.span`
    color: ${Common.colors.notiColor};
    font-size: 16px;
    line-height: 140%;
`;

const SectionTitle = styled.span`
    color: ${Common.colors.normalTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
`;

const YearRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 0;
`;

const YearLabel = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
`;

const YearInputWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100px;
`;

const YearSuffix = styled.span`
    font-size: 16px;
    color: #140c2c;
`;

const SubmitButton = styled.button`
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% - 32px);
    padding: 12px 14px;
    border-radius: 12px;
    background: #2275ee;
    color: #ffffff;
    font-size: 18px;
    font-weight: 400;
    cursor: pointer;
    transition: background-color 0.2s;
    z-index: 10;

    &:disabled {
        opacity: 0.8;
        cursor: not-allowed;
    }
`;

const BottomSpace = styled.div`
    width: 100%;
    height: 80px;
    margin-bottom: 120px;
`;

const CertPreviewWrap = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 0;
`;

const CertPreviewImg = styled.img`
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const LimitText = styled.div<{ $visible?: boolean }>`
    position: absolute;
    right: 12px;
    bottom: 8px;
    font-size: 12px;
    color: #b8b8b8;
    pointer-events: none;
    display: ${({ $visible }) => ($visible ? "block" : "none")};
`;

const ApproveCheckContainer = styled.div`
    width: 100%;
    border-radius: 12px;
    background: #fff;
`;

const ApproveCheckContentWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ApproveCheckIconImage = styled.img`
    width: 32px;
    height: 32px;
`;

const ApproveCheckStatusText = styled.span`
    font-size: 12px;
    color: #ff6b6b;
    white-space: pre-line;
`;
